/**
 * Debug Logger Utility
 * 
 * Enables detailed logging when VITE_DEBUG=true
 * Helps troubleshoot production issues without exposing logs by default
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug'

interface LogContext {
  component?: string
  action?: string
  user?: string
  data?: any
}

class DebugLogger {
  private isDebugEnabled: boolean

  constructor() {
    this.isDebugEnabled = import.meta.env.VITE_DEBUG === 'true'
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 8)
    const emoji = this.getLogEmoji(level)
    const contextStr = context ? ` [${Object.entries(context).map(([k, v]) => `${k}=${v}`).join(', ')}]` : ''
    
    return `${emoji} ${timestamp} ${message}${contextStr}`
  }

  private getLogEmoji(level: LogLevel): string {
    switch (level) {
      case 'info': return 'ℹ️'
      case 'warn': return '⚠️'
      case 'error': return '❌'
      case 'debug': return '🔍'
      default: return '📝'
    }
  }

  info(message: string, context?: LogContext) {
    if (this.isDebugEnabled) {
      console.log(this.formatMessage('info', message, context))
    }
  }

  warn(message: string, context?: LogContext) {
    if (this.isDebugEnabled) {
      console.warn(this.formatMessage('warn', message, context))
    }
  }

  error(message: string, context?: LogContext) {
    // Always log errors, even if debug is disabled
    console.error(this.formatMessage('error', message, context))
  }

  debug(message: string, context?: LogContext) {
    if (this.isDebugEnabled) {
      console.log(this.formatMessage('debug', message, context))
    }
  }

  // Utility methods for common scenarios
  supabaseRequest(operation: string, table: string, context?: any) {
    this.debug(`Supabase ${operation}`, {
      component: 'Supabase',
      action: operation,
      data: { table, ...context }
    })
  }

  supabaseResponse(operation: string, result: { data?: any, error?: any }) {
    if (result.error) {
      this.error(`Supabase ${operation} failed`, {
        component: 'Supabase',
        action: operation,
        data: result.error
      })
    } else {
      this.info(`Supabase ${operation} success`, {
        component: 'Supabase',
        action: operation,
        data: { count: Array.isArray(result.data) ? result.data.length : 'single' }
      })
    }
  }

  authEvent(event: string, user?: any) {
    this.info(`Auth ${event}`, {
      component: 'Auth',
      action: event,
      user: user?.email || 'anonymous'
    })
  }

  slotAction(action: string, slotId: string, user?: string, result?: any) {
    if (result?.success) {
      this.info(`Slot ${action} success`, {
        component: 'VolunteerSlot',
        action,
        data: { slotId, user }
      })
    } else {
      this.error(`Slot ${action} failed`, {
        component: 'VolunteerSlot',
        action,
        data: { slotId, user, error: result?.error }
      })
    }
  }

  // Check if debug is enabled
  isEnabled(): boolean {
    return this.isDebugEnabled
  }

  // Enable/disable debug at runtime
  setEnabled(enabled: boolean) {
    this.isDebugEnabled = enabled
    this.info(`Debug logging ${enabled ? 'enabled' : 'disabled'}`)
  }
}

// Export singleton logger
export const logger = new DebugLogger()
export default logger